/**
 * IL FLUSSO DEL GIOCATORE: la spinta GIOCATA_CAMBIATA dal servizio al sito.
 *
 * UNO PER GIOCATA IN ATTESA, NON UNO PER GIOCATORE. E' il confine scritto in
 * tempo-reale.md e va tenuto: un giocatore che naviga il sito non ha nessun
 * canale aperto. Il canale si apre solo quando una SUA giocata (o di un suo
 * giocatore, per l'agenzia) sta aspettando qualcosa -- in accettazione o
 * riproposta -- e si chiude appena e' decisa. Quante connessioni ci sono lo
 * decide quanto lavoro c'e' in coda, non quanti clienti esistono.
 *
 * ====================================================================
 *  UNO PER PAGINA, NON UNO PER COMPONENTE
 * ====================================================================
 * Tre pezzi del sito mostrano le giocate che aspettano -- la striscia sotto la
 * schedina, la lista scommesse, la spia in alto -- e spesso stanno nella
 * stessa pagina. Se ognuno aprisse il suo flusso, la stessa giocata avrebbe
 * tre connessioni. Quindi il registro sta qui, una volta per pagina: ogni
 * pezzo dice quali giocate vede (`segui(nome, giocate)`), e questo modulo
 * tiene UN flusso per ogni giocata in attesa che qualcuno sta guardando.
 *
 * La spinta arriva qui e diventa l'evento `giocataCambiata` sulla finestra:
 * e' lo stesso evento che i tre pezzi gia' ascoltano quando una giocata parte
 * dalla schedina o si risponde a una riproposta. Nessuno di loro sa che
 * esiste un flusso: sanno che "qualcosa e' cambiato" e rileggono con il loro
 * gettone. La spinta non porta il dato -- vedi Spinta.java sul servizio -- e
 * qui non si inventa niente.
 *
 * ====================================================================
 *  IL BIGLIETTO, E PERCHE' LA RICONNESSIONE E' NOSTRA
 * ====================================================================
 * EventSource non puo' mandare intestazioni, quindi niente gettone: si chiede
 * un biglietto usa-e-getta con una POST normale (autenticata, legata alla
 * giocata) e quello viaggia nell'indirizzo. Si brucia all'uso. Per questo il
 * ritentativo automatico di EventSource va SPENTO: riaprirebbe lo stesso
 * indirizzo col biglietto gia' bruciato, rifiutato all'infinito. La
 * riconnessione la facciamo noi, con un biglietto nuovo e pause che crescono
 * -- e ogni riconnessione e' un ricontrollo che la giocata sia ancora nostra e
 * stia ancora aspettando.
 *
 * ====================================================================
 *  IL GIRO A TEMPO NON SPARISCE: RALLENTA
 * ====================================================================
 * Una spinta persa non costa niente perche' il giro a tempo resta acceso,
 * lento, anche col flusso vivo (`giro()`). Torna veloce -- ogni 5 secondi --
 * solo quando una giocata in attesa NON ha il flusso vivo: il servizio non e'
 * aggiornato, un proxy lo taglia, la rete e' caduta. Cosi' il peggio che puo'
 * succedere e' tornare a com'era prima.
 */
import axios from 'axios';
import { gateway, intestazioni } from './ticketsApi';

const BASE = '/v1/ticket-service/flusso';

/** Gli stati in cui una giocata sta aspettando qualcosa, e vale un flusso. */
export const ASPETTANO = ['DA_ACCETTARE', 'RIPROPOSTA'];

/* Le pause fra un tentativo e l'altro, con un po' di caso dentro: senza,
   dopo un riavvio del servizio tutte le schermate ritenterebbero allo stesso
   millesimo. */
const PAUSE_MS = [1000, 2000, 4000, 8000, 15000, 30000];

/* Le risposte del biglietto che vogliono dire "lascia stare questa giocata":
   e' stata decisa mentre riaprivamo, non esiste, o non e' nostra. Non sono
   guasti da ritentare: si chiude e si avvisa chi la mostra di rileggere. */
const LASCIA = ['FLUSSO_GIOCATA_NON_IN_ATTESA', 'TICKET_NOT_FOUND', 'FLUSSO_GIOCATA_NON_TUA'];

/** nome del pezzo di pagina -> insieme dei codici che vede in attesa */
const fonti = new Map();
/** codice -> { es, vivo, tentativo, timer } */
const flussi = new Map();

/* Quando un flusso si accende o cade, chi disegna una spia deve saperlo:
   evento `flussoCambiato` sulla finestra, senza dati (si rilegge
   statoCopertura()). E una riga nella console per ogni passaggio, cosi' da
   un browser si vede in dieci secondi se il canale e' aperto o se la pagina
   sta andando a giro -- senza strumenti e senza indovinare. */
function cambiato() {
  window.dispatchEvent(new Event('flussoCambiato'));
}
const dillo = (cosa, ...resto) => {
  if (typeof console !== 'undefined' && console.info) console.info('[flusso giocate] ' + cosa, ...resto);
};

/**
 * Un pezzo di pagina dice quali giocate sta mostrando. Si chiama a ogni
 * caricamento, con TUTTE le righe: qui si tengono solo quelle in attesa. Con
 * un elenco vuoto il pezzo si cancella (anche allo smontaggio).
 */
export function segui(fonte, giocate) {
  const codici = new Set();
  (giocate || []).forEach((g) => {
    if (g && g.ticketCode && ASPETTANO.includes(g.status)) codici.add(g.ticketCode);
  });
  if (codici.size) fonti.set(fonte, codici); else fonti.delete(fonte);
  allinea();
  cambiato();
}

function volute() {
  const tutte = new Set();
  fonti.forEach((codici) => codici.forEach((c) => tutte.add(c)));
  return tutte;
}

/** Apre i flussi che mancano, chiude quelli che nessuno guarda piu'. */
function allinea() {
  if (typeof window === 'undefined' || typeof window.EventSource === 'undefined') return;
  const v = volute();
  v.forEach((codice) => { if (!flussi.has(codice)) apri(codice); });
  Array.from(flussi.keys()).forEach((codice) => { if (!v.has(codice)) chiudi(codice); });
}

function apri(codice) {
  const f = { es: null, vivo: false, tentativo: 0, timer: null };
  flussi.set(codice, f);
  attacca(codice, f);
}

async function attacca(codice, f) {
  if (flussi.get(codice) !== f) return;   // chiuso nel frattempo

  let biglietto;
  try {
    const { data } = await axios.post(`${gateway()}${BASE}/biglietto-giocata`,
        { ticketCode: codice }, { headers: intestazioni() });
    biglietto = data && data.biglietto;
  } catch (e) {
    const d = e && e.response && e.response.data;
    if (d && LASCIA.includes(d.code)) {
      dillo('giocata ' + codice + ': non aspetta piu\' (' + d.code + '), la lascio');
      chiudi(codice);
      avvisa(codice);
      return;
    }
    /* Niente biglietto: il servizio non risponde, o non e' ancora quello
       nuovo. Si riprova con le pause, e intanto il giro va veloce. */
    dillo('giocata ' + codice + ': biglietto rifiutato (' + ((d && d.code) || (e && e.response && e.response.status) || (e && e.message) || '?') + '), riprovo');
    riprova(codice, f);
    return;
  }
  if (flussi.get(codice) !== f) return;
  if (!biglietto) { riprova(codice, f); return; }

  const es = new window.EventSource(
      `${gateway()}${BASE}/giocata?biglietto=${encodeURIComponent(biglietto)}`);
  f.es = es;

  /* APERTO arriva dal servizio, non dal browser: `onopen` dice «ho una
     risposta», APERTO dice «le spinte mi arrivano davvero». Da qui il flusso
     conta come vivo e il giro rallenta. */
  es.addEventListener('APERTO', () => {
    if (f.es !== es) return;
    f.vivo = true;
    f.tentativo = 0;
    dillo('giocata ' + codice + ': in diretta');
    cambiato();
    /* Alla riconnessione si rilegge: se in mezzo e' successo qualcosa, si
       trova adesso. E' il motivo per cui una spinta persa non costa niente. */
    if (f.riaperto) avvisa(codice);
    f.riaperto = true;
  });
  es.addEventListener('GIOCATA_CAMBIATA', (ev) => {
    dillo('giocata ' + ((ev && ev.data) || codice) + ': e\' cambiata, rileggo');
    avvisa((ev && ev.data) || codice);
  });
  es.addEventListener('BATTITO', () => { /* tiene viva la linea, niente da fare */ });

  es.onerror = () => {
    /* SI CHIUDE A MANO: EventSource riproverebbe da solo con il biglietto
       gia' bruciato. La riconnessione e' nostra. Il servizio chiude lui il
       flusso quando la giocata e' decisa: arriviamo qui, riproviamo, e la
       rotta del biglietto ci dice che non aspetta piu' -- e si lascia. */
    es.close();
    if (f.es === es) f.es = null;
    const eraVivo = f.vivo;
    f.vivo = false;
    cambiato();
    if (flussi.get(codice) !== f) return;
    dillo('giocata ' + codice + ': flusso ' + (eraVivo ? 'chiuso' : 'non aperto') + ', riprovo fra un po\'');
    riprova(codice, f);
  };
}

function riprova(codice, f) {
  if (flussi.get(codice) !== f) return;
  const base = PAUSE_MS[Math.min(f.tentativo, PAUSE_MS.length - 1)];
  const fra = Math.round(base * (0.8 + Math.random() * 0.4));
  f.tentativo += 1;
  if (f.timer) clearTimeout(f.timer);
  f.timer = setTimeout(() => { f.timer = null; attacca(codice, f); }, fra);
}

function chiudi(codice) {
  const f = flussi.get(codice);
  if (!f) return;
  flussi.delete(codice);
  if (f.timer) clearTimeout(f.timer);
  if (f.es) { try { f.es.close(); } catch (e) { /* era gia' chiuso */ } }
  cambiato();
}

/** La spinta diventa l'evento che i pezzi di pagina gia' ascoltano. */
function avvisa(codice) {
  window.dispatchEvent(new CustomEvent('giocataCambiata', { detail: { ticketCode: codice } }));
}

/**
 * Vero se ogni giocata in attesa che qualcuno guarda ha il flusso vivo.
 * Vero anche quando nessuna aspetta: non c'e' niente da coprire.
 */
export function copertura() {
  const v = volute();
  for (const codice of v) {
    const f = flussi.get(codice);
    if (!f || !f.vivo) return false;
  }
  return true;
}

/**
 * Per una spia: 'niente' se nessuna giocata aspetta (non c'e' niente da
 * coprire), 'diretta' se ogni giocata in attesa ha il flusso vivo, 'giro'
 * se almeno una aspetta senza flusso e la pagina sta andando a giro.
 */
export function statoCopertura() {
  if (volute().size === 0) return 'niente';
  return copertura() ? 'diretta' : 'giro';
}

/** Quante giocate questa pagina sta seguendo col flusso vivo (per chi guarda). */
export function flussiVivi() {
  let n = 0;
  flussi.forEach((f) => { if (f.vivo) n += 1; });
  return n;
}

/**
 * IL GIRO A TEMPO, con due passi: `veloce` finche' c'e' una giocata in attesa
 * senza flusso vivo, `lento` altrimenti. Non si rilegge a scheda nascosta.
 * Torna la funzione che lo ferma.
 */
export function giro(ricarica, opzioni = {}) {
  const veloce = opzioni.veloce || 5000;
  const lento = opzioni.lento || 30000;
  let ultimo = Date.now();
  const t = setInterval(() => {
    if (typeof document !== 'undefined' && document.hidden) return;
    const passato = Date.now() - ultimo;
    if ((passato >= veloce && !copertura()) || passato >= lento) {
      ultimo = Date.now();
      ricarica();
    }
  }, Math.min(veloce, lento));
  return () => clearInterval(t);
}
